import { cloneDeep } from 'lodash'

import { getConvertedData } from '../../../../../models/interface.helper'
import { getUserFullName } from '../../../../../helpers/helpers'
import { httpService } from '../../../../../services/httpService'

import FirestoreService from '../../../../../services/firestoreService'

import { ScoreCardModel } from '../../../../../models/score-cards/score-cards.model'
import { RegistrationFeesModel } from '../../../../../models/registration-fees/registrationFees.model'
import {
  HORSE_NAME_DEFAULT,
  IHorseSelected,
  IJudge,
  IRegistrationByDayInterface,
  IScoreSheet,
  IScoreStatus,
} from '../../../../../models/registrations-by-day/registrationByDay.interface'
import {
  IScoreCardSection,
  IScoreCardType,
} from '../../../../../models/score-cards/score-cards.interface'
import {
  IScoreCardDiscipline,
  RegistrationFeesType,
} from '../../../../../models/event-fees/event-fees.interface'
import { IUserPublicInterface } from '../../../../../models/users-public/users-public.interface'

import { dummySheets } from '../../data/sheets'

import { CONST } from '../../../../../const/const'
import { EventFeesModel } from '../../../../../models/event-fees/event-fees.model'
import { IRiderTeamMemberInterface } from '../../../../../models/rider-team-member/riderTeamMember.interface'
import { IHorseData } from '../../../../../models/horse/horse.interface'
import { IBreakRegistrationByDay } from '../../../../../models/events/event.interface'
import { MEMBER_ROLES_TYPE } from '../../../../../const/ui/ui-register.const'

import { scoreStatuses } from '../../../../../const/score-card-const'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const DUMMY_REGISTRATION_FEES_STATS = [
  { ...new RegistrationFeesModel(), loading: true },
  { ...new RegistrationFeesModel(), loading: true },
  { ...new RegistrationFeesModel(), loading: true },
  { ...new RegistrationFeesModel(), loading: true },
]

export const sortByOrder = (array: any[]) => {
  return cloneDeep(array).sort((a, b) => {
    // OT riders have order = 0 - should appear at the top
    if (a.isOutOfTurn && !b.isOutOfTurn) return -1
    if (!a.isOutOfTurn && b.isOutOfTurn) return 1

    if (a.order === 0 && b.order === 0) return 0
    if (a.order === 0) return 1
    if (b.order === 0) return -1

    return a.order - b.order
  })
}

export const sortByOutOfTurn = (array: any[]) => {
  return cloneDeep(array).sort((a, b) => {
    // OT riders have order = 0 - should appear at the top
    if (a.isOutOfTurn && !b.isOutOfTurn) return -1
    if (!a.isOutOfTurn && b.isOutOfTurn) return 1
    return a.isOutOfTurn - b.isOutOfTurn
  })
}

const getSections = (x: IRegistrationByDayInterface) => x.score?.judges?.[0]?.customSections ?? []

const hasScoreStatus = (x: IRegistrationByDayInterface) => {
  const status = x.score?.scoreStatus
  return !!status && scoreStatuses.includes(status)
}

const toNum = (v: unknown) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export const sortResultsByTime = (
  array: IRegistrationByDayInterface[],
  direction: 'asc' | 'desc',
  mode: 'fastest' | 'optimal'
) => {
  const dir = direction === 'asc' ? 1 : -1

  // Step 3: check if a jump off exists and its totalTime is 0
  // these riders should be placed lower, but still before scoreStatuses
  const hasJumpOffZeroTime = (x: IRegistrationByDayInterface) => {
    const sections = getSections(x)
    const jumpOff = sections.find((s) => s.type === 'jump off')
    if (!jumpOff) return false
    return Number(jumpOff.totalTime ?? 0) === 0
  }

  // Determine which section should be used for sorting
  // Rules:
  // 1. If jump off exists and totalTime > 0 → use jump off
  // 2. If jump off exists and totalTime = 0 → use the previous section
  // 3. If no jump off exists → use the last section
  const getTargetSection = (x: IRegistrationByDayInterface) => {
    const sections = getSections(x)
    if (!sections.length) return null

    const jumpOffIndex = sections.findIndex((s) => s.type === 'jump off')

    if (jumpOffIndex !== -1) {
      const jumpOff = sections[jumpOffIndex]
      const jumpTime = Number(jumpOff.totalTime ?? 0)

      if (jumpTime > 0) return jumpOff
      if (jumpOffIndex > 0) return sections[jumpOffIndex - 1]
    }

    return sections[sections.length - 1]
  }

  // if totalFaultsCount >= 4 -> push lower
  const hasFourOrMoreFaults = (x: IRegistrationByDayInterface) => {
    const sections = getSections(x)
    if (!sections?.[0]) return false
    return toNum(sections?.[0]?.totalFaultsCount) >= 4
  }

  // Group priority:
  // 0 → normal riders
  // 1 → jumpOff with totalTime = 0 OR totalFaultsCount >= 4
  // 2 → riders with scoreStatus
  // 3 → scratched riders (always last)
  const groupRank = (x: IRegistrationByDayInterface) => {
    if (x.isScratched) return 3
    if (hasScoreStatus(x)) return 2
    if (hasJumpOffZeroTime(x) || hasFourOrMoreFaults(x)) return 1
    return 0
  }

  const getSortValue = (section: IScoreCardSection) => {
    const time = toNum(section?.totalTime)
    const faults = toNum(section?.totalFaultsCount)
    const maxTime = toNum(section?.maxTime)

    if (mode === 'optimal') {
      // Smaller gap is better (closest to maxTime).
      // Using abs to handle cases where totalTime > maxTime.
      return Math.abs(maxTime - (time + faults))
    }

    // mode === 'fastest'
    return time + faults
  }

  return [...array].sort((a, b) => {
    // Step 1–3: compare group priority
    const rankA = groupRank(a)
    const rankB = groupRank(b)
    if (rankA !== rankB) return rankA - rankB

    // Step 4: compare by performance metric inside the same group
    const sectionA = getTargetSection(a)
    const sectionB = getTargetSection(b)

    if (!sectionA && !sectionB) return 0
    if (!sectionA) return 1
    if (!sectionB) return -1

    const timeA = toNum(sectionA.totalTime)
    const timeB = toNum(sectionB.totalTime)

    // Riders with totalTime = 0 should appear below riders with valid times
    const zeroA = timeA === 0
    const zeroB = timeB === 0
    if (zeroA !== zeroB) return zeroA ? 1 : -1

    const valueA = getSortValue(sectionA)
    const valueB = getSortValue(sectionB)

    if (valueA === valueB) return 0

    // For optimal: smaller is better, so direction should usually be 'asc'
    // We still allow direction override via `dir`.
    return (valueA - valueB) * dir
  })
}

export const sortByTotalPoints = (array: IRegistrationByDayInterface[], direction = 'desc') => {
  return cloneDeep(array).sort((a, b) => {
    const totalPointsA = Number(a.score?.totalPoints || 0)
    const totalPointsB = Number(b.score?.totalPoints || 0)
    const isWithdrewA: number =
      a.score?.scoreStatus && scoreStatuses.includes(a.score?.scoreStatus) ? 1 : 0
    const isWithdrewB: number =
      b.score?.scoreStatus && scoreStatuses.includes(b.score?.scoreStatus) ? 1 : 0
    const isScratchedA: number = a.isScratched ? 1 : 0
    const isScratchedB: number = b.isScratched ? 1 : 0

    if (isWithdrewA !== isWithdrewB) {
      return isWithdrewA - isWithdrewB
    }

    if (isScratchedA !== isScratchedB) {
      return isScratchedA - isScratchedB
    }

    if (totalPointsA === 0 && totalPointsB === 0) {
      return 0
    }
    if (totalPointsA === 0) {
      return 1
    }
    if (totalPointsB === 0) {
      return -1
    }

    return direction === 'asc' ? totalPointsA - totalPointsB : totalPointsB - totalPointsA
  })
}

export const getJudgesAndSheet = async ({
  discipline,
  sheetId,
  judgeIds,
  users,
}: {
  discipline: IScoreCardDiscipline
  sheetId?: string
  judgeIds: string[]
  users: IUserPublicInterface[]
}) => {
  const judges: IJudge[] = []
  let sheet: IScoreSheet = { id: '', name: '', type: null }

  if (discipline === 'Dressage') {
    const current = dummySheets.find((current) => current.id === sheetId)
    judgeIds?.forEach((judge) => {
      const user = users.find((user) => user.id === judge)

      judges.push({
        id: judge,
        name: user ? getUserFullName(user) : 'N/A',
        picture: user?.userProfilePicture || '',
        furtherRemarks: cloneDeep(current?.furtherRemarks) || '',
        sections: cloneDeep(current?.sections) || [],
        customSections: [],
        errors: cloneDeep(current?.errors) || 0,
        scoreCardType: null,
        signatureDate: null,
        signatureUrl: null,
      })
    })

    sheet = {
      id: current?.id ?? '',
      name: current?.name ?? '',
      type: (current?.type as IScoreCardType) ?? null,
    }
  }
  if (discipline === 'Hunter Jumper' && sheetId) {
    const scoreCardSnaps = await FirestoreService.getItem(COLLECTIONS.SCORE_CARDS.NAME, sheetId)
    const current = getConvertedData(ScoreCardModel.fromFirestoreDoc(scoreCardSnaps).toObject())
    judgeIds?.forEach((judge) => {
      const user = users.find((user) => user.id === judge)

      judges.push({
        id: judge,
        name: user ? getUserFullName(user) : 'N/A',
        picture: user?.userProfilePicture ?? '',
        furtherRemarks: current.furtherRemarks,
        sections: [],
        customSections: current?.sections ?? [],
        showJumpOff: current.showJumpOff,
        fenceHeight: current.fenceHeight,
        scoreCardType: current.scoreCardType,
        signatureDate: null,
        signatureUrl: null,
      })
    })

    sheet = {
      id: current?.id ?? '',
      name: current?.name ?? '',
      type: current?.type ?? null,
    }
  }

  return { judges, sheet }
}

export const getRegistrationFee = (
  registrationFees: RegistrationFeesType[],
  uuid: string,
  parentId?: string
) => {
  let current: RegistrationFeesType = new EventFeesModel().registrationFees[0]

  if (parentId) {
    const parentIndex = registrationFees?.findIndex(
      (item: RegistrationFeesType) => item.uuid === parentId
    )
    registrationFees[parentIndex]?.children?.forEach((registrationFee: RegistrationFeesType) => {
      if (registrationFee.uuid === uuid) {
        current = registrationFee
      }
    })
  } else {
    registrationFees?.forEach((registrationFee: RegistrationFeesType) => {
      if (registrationFee.uuid === uuid) current = registrationFee
    })
  }

  return current
}

export const getSortedAndUnique = (riders: IRegistrationByDayInterface[]) => {
  const uniqueRegistrationsByDay: IRegistrationByDayInterface[] = []
  const sorted = sortByOrder(riders)

  sorted.forEach((item) => {
    const isRiderIdExist = uniqueRegistrationsByDay.find(
      (rider) =>
        rider.uuid === item.uuid && rider.riderId === item.riderId && rider.horseId === item.horseId
    )

    if (!isRiderIdExist) uniqueRegistrationsByDay.push(item)
  })

  return uniqueRegistrationsByDay
}

export const getTeamMemberName = (
  riderTeamMembers: IRiderTeamMemberInterface[],
  registeredUserByDay: IRegistrationByDayInterface | IBreakRegistrationByDay,
  type: MEMBER_ROLES_TYPE
) => {
  return (
    riderTeamMembers?.find(
      (member) =>
        registeredUserByDay.registrationDocId === member.registrationDocId &&
        registeredUserByDay.riderId === member.riderId &&
        registeredUserByDay.horsesSelected?.some((horse) => horse.id === member.horseId) &&
        type === member.teamMemberRole
    )?.teamMemberName || `No ${type}`
  )
}

export const getHorseNames = (horses: IHorseData[] | null, horseIds?: IHorseSelected[]) => {
  return horseIds?.[0]
    ? horseIds.map(
        (item) => horses?.find((horse) => horse.id === item.id)?.horseName || HORSE_NAME_DEFAULT
      )
    : [HORSE_NAME_DEFAULT]
}

export const getEntryName = (rider: IRegistrationByDayInterface | IBreakRegistrationByDay) => {
  return rider.isScratched
    ? `--${rider.registrationByDayName}--`
    : rider.registrationByDayName || 'N/A'
}

export const updateScoreStatus = async (
  registrationsByDay: IRegistrationByDayInterface[] | null,
  fee: RegistrationFeesType | null
) => {
  const registrationsByDayToUpdate: IRegistrationByDayInterface[] = []
  const registrationsByDayUpdated: IRegistrationByDayInterface[] =
    registrationsByDay?.map((registrationByDay) => {
      if (
        fee?.uuid === registrationByDay.uuid &&
        !registrationByDay.score?.totalPoints &&
        !registrationByDay.score?.scoreStatus &&
        !registrationByDay.isScratched &&
        (fee?.scoreCard === 'Default' ||
          fee?.scoreCard === 'Equitation' ||
          fee?.scoreCard === 'Hunters')
      ) {
        const current = {
          ...registrationByDay,
          score: {
            ...registrationByDay.score,
            scoreStatus: 'Did Not Place' as IScoreStatus,
          },
        }
        registrationsByDayToUpdate.push(current)
        return current
      } else {
        return registrationByDay
      }
    }) || []

  // Update firestore collection on BE by batch
  if (registrationsByDayToUpdate?.[0]) {
    await httpService({
      url: 'update_registrations_by_day',
      method: 'POST',
      data: {
        registrationsByDay: registrationsByDayToUpdate,
      },
    })
  }

  return registrationsByDayUpdated
}
